import { startCase } from "lodash";
import { t } from "@/i18n-js/instance";
import { Typography } from "@circle-react-shared/uikit/Typography";
import {
  formatValueDynamically,
  getFormattedValue,
} from "../../../../utils/previewValueFormatterUtils";
import { PreviewCardLayout } from "./PreviewCardLayout";

type SpaceParams = {
  name?: string;
  space_group_name?: string;
  space_type?: string;
  course_type?: string;
  access?: string;
  auto_rsvp_invited_attendees?: boolean;
  notification_type?: string | string[];
  default_notification_setting?: boolean;
  default_in_app_notification_setting?: boolean;
  default_mobile_notification_setting?: boolean;
} & Record<string, string | number | boolean | string[] | undefined>;

type SpaceFieldKey =
  | "name"
  | "space_group_name"
  | "space_type"
  | "course_type"
  | "access"
  | "auto_rsvp_invited_attendees"
  | "notification_type";

interface SpaceFieldConfig {
  defaultValue?: string | string[] | boolean;
  label: string;
  type: "string" | "humanize" | "boolean" | "array";
}

interface SpaceCardProps {
  params: SpaceParams;
  url: string;
}

export const SPACE_FIELDS: Record<SpaceFieldKey, SpaceFieldConfig> = {
  name: { label: t("name", { defaultValue: "Name" }), type: "string" },
  space_group_name: {
    label: t("space_groups.space_group", { defaultValue: "Space Group" }),
    type: "string",
  },
  space_type: {
    label: t("spaces.form.create.space_types.label"),
    type: "humanize",
    defaultValue: t("settings.spaces.space_types.basic"),
  },
  course_type: {
    label: t("spaces.form.create.course_types.label"),
    type: "string",
  },
  access: {
    label: t("spaces.form.create.space_access_label", {
      defaultValue: "Access",
    }),
    type: "humanize",
  },
  auto_rsvp_invited_attendees: {
    label: t("spaces.form.event_auto_rsvp_enabled"),
    type: "boolean",
  },
  notification_type: {
    label: t("spaces.form.create.notification_types.label"),
    type: "string",
  },
};

const NOTIFICATION_KEYS = [
  "default_notification_setting",
  "default_in_app_notification_setting",
  "default_mobile_notification_setting",
];

const HIDDEN_EXTRA_KEYS = ["space_group_id", ...NOTIFICATION_KEYS];

export const SpaceCard = ({ params, url }: SpaceCardProps) => {
  const filteredExtras = Object.entries(params).filter(
    ([key, value]) =>
      !(key in SPACE_FIELDS) &&
      !HIDDEN_EXTRA_KEYS.includes(key) &&
      value != null,
  );

  const notificationLabels: Record<string, string> = {
    default_notification_setting: t(
      "help_widget.copilot.preview_cards.notifications.email",
      { defaultValue: "Email" },
    ),
    default_in_app_notification_setting: t(
      "help_widget.copilot.preview_cards.notifications.in_app",
      { defaultValue: "In-app" },
    ),
    default_mobile_notification_setting: t(
      "help_widget.copilot.preview_cards.notifications.mobile",
      { defaultValue: "Mobile" },
    ),
  };

  const enabledNotifications = NOTIFICATION_KEYS.reduce<string[]>(
    (acc, key) => {
      const isEnabled = key in params ? params[key] === true : false;
      if (isEnabled) {
        acc.push(notificationLabels[key]);
      }
      return acc;
    },
    [],
  );

  const hasNotifications = enabledNotifications.length > 0;

  return (
    <PreviewCardLayout
      title={t("help_widget.copilot.preview_cards.space_card_title")}
      url={url}
    >
      <ul className="flex flex-col leading-5">
        {(
          Object.entries(SPACE_FIELDS) as Array<
            [SpaceFieldKey, SpaceFieldConfig]
          >
        ).flatMap(([key, keyProps]) => {
          const hasValueInParams = key in params && params[key] != null;
          const value = hasValueInParams ? params[key] : keyProps.defaultValue;

          if (value == null) return [];

          return [
            <li key={key} className="text-darkest !pb-0">
              <Typography.BodySm color="text-darkest">
                <strong>{keyProps.label}:</strong>{" "}
                {getFormattedValue(value, keyProps.type)}
              </Typography.BodySm>
            </li>,
          ];
        })}
        {hasNotifications && (
          <li className="text-darkest !pb-0">
            <Typography.BodySm color="text-darkest">
              <strong>
                {t("help_widget.copilot.preview_cards.space.notifications", {
                  defaultValue: "New notifications",
                })}
                :
              </strong>{" "}
              {enabledNotifications.join(", ")}
            </Typography.BodySm>
          </li>
        )}
        {filteredExtras.map(([key, value]) => {
          if (value == null) return null;

          const displayValue = value;

          return (
            <li key={key} className="text-darkest !pb-0">
              <Typography.BodySm color="text-darkest">
                <strong>{startCase(key)}:</strong>{" "}
                {formatValueDynamically(displayValue)}
              </Typography.BodySm>
            </li>
          );
        })}
      </ul>
    </PreviewCardLayout>
  );
};
